import { getCurrencySymbol, Location } from "@angular/common";
import { Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { FiltersComponent } from "@app/private/filters/filters.component";
import { PageEvent } from "@shared/angular2-datatable/data-table.directive";
import { AuctionStatus, ClickarPath, Permission, SessionKey } from "@shared/clickar-constants";
import { ConfirmDialogComponent } from "@shared/dialogs/confirm-dialog/confirm-dialog.component";
import { AuctionDTO, AuctionFullDTO, TableData } from "@shared/model/auction.model";
import { LotDTO, LotFullDTO, LotVehicle, Purchase } from "@shared/model/lotDTO";
import { MqttResponse } from "@shared/model/mqtt.response";
import { UserDTO } from "@shared/model/user.model";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { ApiService } from "@shared/services/api.service";
import { AuctionService } from "@shared/services/auction/auction.service";
import { FileService } from "@shared/services/file/file.service";
import { MqttService } from "@shared/services/mqtt/mqtt.service";
import { SnackbarService } from "@shared/services/snackbar.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { TemplateService } from "@shared/services/template/template.service";
import { UsersService } from "@shared/services/users/users.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { saveAs } from "file-saver-es";
import { fromEvent, timer } from "rxjs";
import { take } from "rxjs/operators";
const enum DefaultRowsOnPage {
	single = 12,
	multi = 12
};

const enum DefaultLimit {
	single = 36,
	multi = 36
};

const enum DefaultOffset {
	single = 0,
	multi = 0
};

interface PagingArchive {
	[key: string]: {
		single?: { mfActivePage: number, rowsOnPage: number };
		multi?: { mfActivePage: number, rowsOnPage: number };
	}
};
@Component({
	selector: "app-auction-details",
	templateUrl: "./auction-details.component.html",
	styleUrl: "./auction-details.component.scss",
	standalone: false
})
export class AuctionDetailsComponent implements OnInit, OnDestroy {
	@ViewChild(FiltersComponent) filtersComp: FiltersComponent;
	/** Potential MQTT connection error dialog content */
	@ViewChild('connectError') connectError;
	/** Keeps track of whether we showed the connection error dialog */
	private warningShown = false;
	protected closeTemplateModal = () => { };

	/** Delta time in milliseconds between local machine time and server-provided time. Used for syncing auctions' countdowns */
	protected timeOffset = 0;

	private destroyRef = inject(DestroyRef);
	private snackBarService = inject(SnackbarService);
	private storageService = inject(StorageManagerService);
	private destroyed = false;
	protected ClickarPath = ClickarPath;

	/** Whether the Auction message is open or not */
	protected messagesOpen = false;

	/** Set of lot numbers for which cars lists are open at the moment */
	private openLotLists = new Set<number>();

	/** Lot number for which cars list is open at the moment (and visibie in mobile's bottom-sheet) */
	protected mobileCarsList: number | null = null;

	protected openCarList = (lot: number) => {
		this.openLotLists.add((this.mobileCarsList = lot));
		this.storageService.setSessionItem(
			SessionKey.OPEN_CAR_LOT_ACCORDIONS,
			Array.from(this.openLotLists)
		);
		this.storageService.setSessionItem(
			SessionKey.OPEN_CAR_LOT_ACCORDION_MOBILE,
			lot
		);
	};

	protected getCurrencySymbol = getCurrencySymbol;

	protected closeCarList = (lot: number) => {
		this.openLotLists.delete(lot);
		this.mobileCarsList = null;
		this.storageService.setSessionItem(
			SessionKey.OPEN_CAR_LOT_ACCORDIONS,
			Array.from(this.openLotLists)
		);
		this.storageService.removeSessionItem(
			SessionKey.OPEN_CAR_LOT_ACCORDION_MOBILE
		);
	};

	protected isCarListOpen(lot: number) { return this.openLotLists.has(lot); }

	protected Math = Math;
	listView = true;

	protected saveOrderBy(multi: boolean, attr: string | string[]) { // NOTE: string[] just to keep TS quiet
		(multi ? this.tableDataMulti : this.tableDataSingle).orderBy = attr as string; // NOTE: casting just to keep TS quiet
		this.storageService.setSessionItem(SessionKey[multi ? "AUCTIONS_MULTI_SORTBY" : "AUCTIONS_SINGLE_SORTBY"], attr);
	}

	protected saveSortOrder(multi: boolean, order: "asc" | "desc") {
		(multi ? this.tableDataMulti : this.tableDataSingle).sortOrder = order;
		this.storageService.setSessionItem(SessionKey[multi ? "AUCTIONS_MULTI_SORT_ORDER" : "AUCTIONS_SINGLE_SORT_ORDER"], order);
	}

	private updateStoredPaging(multi: boolean, mfActivePage = 1, rowsOnPage: number) {
		const savedPaging: PagingArchive = this.storageService.getSessionItem(SessionKey.AUCTIONS_PAGING, {});
		(savedPaging[this.auction.idAuction.toString()] ??= {})[multi ? "multi" : "single"] = { mfActivePage, rowsOnPage: rowsOnPage || (multi ? DefaultRowsOnPage.multi : DefaultRowsOnPage.single) };
		this.storageService.setSessionItem(SessionKey.AUCTIONS_PAGING, savedPaging);
	}

	protected savePaging(pageEvent: PageEvent | any, multi: boolean) { // Note: any just to satisfy Typescript
		const table = this[multi ? "tableDataMulti" : "tableDataSingle"];
		if ((pageEvent as PageEvent).activePage) {
			table.mfActivePage = pageEvent.activePage;
		}
		if ((pageEvent as PageEvent).rowsOnPage) {
			table.rowsOnPage = pageEvent.rowsOnPage;
		}
		this.updateStoredPaging(multi, table.mfActivePage, table.rowsOnPage)
	}

	protected tableDataSingle: TableData = {
		rowsOnPage: DefaultRowsOnPage.single,
		orderBy: "",
		sortOrder: "desc",
		offset: DefaultOffset.single,
		limit: DefaultLimit.single,
		mfActivePage: 1
	};

	protected tableDataMulti: TableData = {
		rowsOnPage: DefaultRowsOnPage.multi,
		orderBy: "",
		sortOrder: "desc",
		offset: DefaultOffset.multi,
		limit: DefaultLimit.multi
	};
	rowsOnPageSet = [DefaultRowsOnPage.single, 24, 36];
	showSearch = false;
	started: boolean;

	// closed = true;

	auction: AuctionDTO;
	auctionFull: AuctionFullDTO;
	singleLot: VehicleDTO[] = [];
	filteredSingles: VehicleDTO[] = [];

	lots: LotFullDTO[];

	multipleLot: LotFullDTO[] = [];
	filteredMultiples: LotFullDTO[] = [];

	/** On landing (and the first trigger  {@link onSingleLevelChange} trigger) the pagination is restored from SessionStorage (if present).\
	 * For instance, if I filtered and set page 4, I still want to see the same page and same filter when I land back on the page.
	 * Aftermaths, when user changes the filters (but not when filters input data has been changed, just like on MQTT update),\
	 * the page is reset to 1 (and stored to SessionStorage), since user is watching new data.
	 */
	onSingleLevelChange = ({ data, filtersHaveChanged }: { data: VehicleDTO[] | Purchase[], filtersHaveChanged: boolean }) => { // NOTE: Purchase serve solo per evitare che Typescript rompa le balle
		const savedPaging: PagingArchive[string] = this.storageService.getSessionItem(SessionKey.AUCTIONS_PAGING, {})[this.auction.idAuction.toString()];
		if (savedPaging?.single) {
			// Checking whether the page is still available: let's say we were on the latest page, but now something is missing.
			// Here, filtered lots still aren't available, hence the check is performed on all the lots. Later, the filter update whould reset everything
			const maxPage = Math.ceil(data.length / (this.tableDataSingle.rowsOnPage || DefaultRowsOnPage.single)) || 1;
			if (savedPaging.single.mfActivePage > maxPage) {
				savedPaging.single.mfActivePage = maxPage;
			}
			Object.assign(this.tableDataSingle, savedPaging.single);
		}
		this.filteredSingles = data as VehicleDTO[];

		/** After the first call to {@link onSingleLevelChange}, this will override it.\
		 * The SessionStorage-archived paging status will be retrieved only on the first attempt */
		this.onSingleLevelChange = ({ data, filtersHaveChanged }: { data: VehicleDTO[] | Purchase[], filtersHaveChanged: boolean }) => {
			this.filteredSingles = data as VehicleDTO[];
			if (filtersHaveChanged) {
				this.updateStoredPaging(false, this.tableDataSingle.mfActivePage = 1, this.tableDataSingle.rowsOnPage); // TODO: farlo solo quando si è cambiato il valore dei filtri. Se è un update degli input dei filtri (come accade agli eventi MQTT) non aggiornare la paginazione
			}
		}
	}

	/** On landing (and the first trigger  {@link onMultiLevelChange} trigger) the pagination is restored from SessionStorage (if present).\
	 * For instance, if I filtered and set page 4, I still want to see the same page and same filter when I land back on the page.
	 * Aftermaths, when user changes the filters (but not when filters input data has been changed, just like on MQTT update),\
	 * the page is reset to 1 (and stored to SessionStorage), since user is watching new data.
	*/
	onMultiLevelChange = ({ data, filtersHaveChanged }: { data: LotFullDTO[], filtersHaveChanged: boolean }) => {
		const savedPaging: PagingArchive[string] = this.storageService.getSessionItem(SessionKey.AUCTIONS_PAGING, {})[this.auction.idAuction.toString()];
		if (savedPaging?.multi) {
			// Checking whether the page is still available: let's say we were on the latest page, but now something is missing.
			// Here, filtered lots still aren't available, hence the check is performed on all the lots. Later, the filter update whould reset everything
			const maxPage = Math.ceil(data.length / (this.tableDataMulti.rowsOnPage || DefaultRowsOnPage.multi)) || 1;
			if (savedPaging.multi.mfActivePage > maxPage) {
				savedPaging.multi.mfActivePage = maxPage;
			}
			Object.assign(this.tableDataMulti, savedPaging.multi);
		}
		this.filteredMultiples = data;

		/** After the first call to {@link onMultiLevelChange}, this will override it.\
		 * The SessionStorage-archived paging status will be retrieved only on the first attempt */
		this.onMultiLevelChange = ({ data, filtersHaveChanged }: { data: LotFullDTO[], filtersHaveChanged: boolean }) => {
			this.filteredMultiples = data;
			if (filtersHaveChanged) {
				this.updateStoredPaging(true, this.tableDataMulti.mfActivePage = 1, this.tableDataMulti.rowsOnPage); // TODO: farlo solo quando si è cambiato il valore dei filtri. Se è un update degli input dei filtri (come accade agli eventi MQTT) non aggiornare la paginazione
			}
		};
	}

	acceptedRegulation: boolean | null = null;
	regulationOpened = false;

	sumMultiVehicles = 0;
	sumSingleObserved = 0;
	sumLotsObserved = 0;

	user: UserDTO;
	isAdmin: boolean;
	showAuctionMessage = true;

	uploadedFile: any;
	uploadedFileId: number;
	logoAuctioneer: any;

	protected toggleListView() {
		this.storageService.setSessionItem(
			SessionKey.AUCTION_DETAIL_LISTVIEW,
			this.listView = !this.listView
		);
	}

	constructor(
		private activatedRouter: ActivatedRoute,
		private auctionService: AuctionService,
		private userService: UsersService,
		private fileService: FileService,
		private router: Router,
		private templateService: TemplateService,
		private apiService: ApiService,
		private dialog: MatDialog,
		private mqttService: MqttService,
		public location: Location,
		protected utils: ClickarUtilsService,
		public wcmService: WcmService
	) {
		apiService.getTimeUsingGET()
			.subscribe(value => {
				this.timeOffset = value - Date.now();
			});
		this.openLotLists = new Set(
			this.storageService.getSessionItem(
				SessionKey.OPEN_CAR_LOT_ACCORDIONS,
				[]
			)
		);

		this.mobileCarsList = this.storageService.getSessionItem(
			SessionKey.OPEN_CAR_LOT_ACCORDION_MOBILE,
			null
		);

		this.listView = this.storageService.getSessionItem(SessionKey.AUCTION_DETAIL_LISTVIEW, true);
		this.tableDataSingle.orderBy = this.storageService.getSessionItem(SessionKey.AUCTIONS_SINGLE_SORTBY, this.tableDataSingle.orderBy || "");
		this.tableDataSingle.sortOrder = this.storageService.getSessionItem(SessionKey.AUCTIONS_SINGLE_SORT_ORDER, this.tableDataSingle.sortOrder || "asc");
		this.tableDataMulti.orderBy = this.storageService.getSessionItem(SessionKey.AUCTIONS_MULTI_SORTBY, this.tableDataMulti.orderBy || "");
		this.tableDataMulti.sortOrder = this.storageService.getSessionItem(SessionKey.AUCTIONS_MULTI_SORT_ORDER, this.tableDataMulti.sortOrder || "asc");
	}
	ngOnInit() {
		this.userService.getProfileOfLoggedUser()
			.pipe(take(1))
			.subscribe(value => {
				this.user = value;
				this.activatedRouter.queryParams.subscribe(value => {
					const idAuction = value.idAuction;
					if (this.user.permission === Permission.VISITOR) {
						this.acceptedRegulation = true;
					} else {
						this.apiService
							.acceptedRegulationUsingGET(idAuction)
							.subscribe(reg => { this.acceptedRegulation = reg; });
					}
					this.apiService
						.getAuctionFullUsingGET(idAuction)
						.subscribe(auc => {
							this.mapAuction(auc);
							/* If the auction is still scheduled, the timer will "open" it and the lots when it starts
							* (this.started has been calculated within mapAuction)
							*/
							if (!this.started) {
								timer(new Date(this.auction.startDate).getTime() - Date.now())
									.pipe(takeUntilDestroyed(this.destroyRef))
									.subscribe(() => {
										this.started = true;
										this.auction.status = AuctionStatus.RUNNING;
										for (const lot of this.multipleLot) {
											lot.lotDTO.calculatedStatus = AuctionStatus.RUNNING;
										}
										for (const lot of this.singleLot) {
											lot.calculatedStatus = AuctionStatus.RUNNING;
										}
									});
							}
							// this.vehicleMarket = auc.lotFullDTO[0].vehiclesDTO[0].idMarket;
							/* this.codeTableService
								.getCTElementsUsingGET(
									CT[CT.COUNTRY_AREA],
									this.vehicleMarket
								)
								.subscribe(value => {
									this.regions = value;
								}); */
						});
				});
			});

		this.userService.isAdmin().subscribe(value => {
			this.isAdmin = value;
		});

		fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
			.pipe(
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.ngOnDestroy();
			});
	}

	openURL(url: any) {
		window.open(url, "_blank");
	}

	// isEnded = (): boolean => this.auction ? new Date(this.auction.endDate) <= new Date() : false;

	goToAuctioneerPresentation(idTemplate?: number) {
		this.router.navigate([
			"private/partner",
			{ idTemplate: idTemplate ?? this.auction.idTemplate }
		]);
	}

	callObserve(lot: LotFullDTO) {
		if (lot.observed) {
			this.apiService
				.observeAuctionLotUsingPOST(
					true,
					this.auction.idAuction,
					lot.lotDTO.idLot
				)
				.subscribe((/* value */) => {
					lot.observed = false;
					this[lot.lotDTO.flgMultiItem ? "sumLotsObserved" : "sumSingleObserved"]--;
				});
		} else {
			this.apiService
				.observeAuctionLotUsingPOST(
					false,
					this.auction.idAuction,
					lot.lotDTO.idLot
				)
				.subscribe((/* value */) => {
					lot.observed = true;
					this[lot.lotDTO.flgMultiItem ? "sumLotsObserved" : "sumSingleObserved"]++;
				});
		}
	}

	observe(element?: VehicleDTO, lotFull?: LotFullDTO) {
		this.callObserve(
			this.lots.find(
				lotFull
					? value => value.lotDTO.idLot === lotFull.lotDTO.idLot
					: value => value.lotDTO.idLot === element.idLot
			));
	}

	getLot(element?: VehicleDTO): LotFullDTO { return this.lots.find(value => value.lotDTO.idLot == element.idLot); }

	observed(idLot: number): boolean {
		if (!this.utils.isNullOrUndefined(idLot)) {
			const lot: LotFullDTO = this.lots.filter(
				value => value.lotDTO.idLot == idLot
			)[0];
			return lot.observed;
		}
		return false;
	}

	openRegulation() {
		const filename = `regulation_${this.auction.auctionName}.pdf`;
		this.auctionService
			.getPdfUsingGET(this.auction.regulation, filename)
			.subscribe((response: Blob) => {
				saveAs(response, filename);
				this.regulationOpened = true;
			});
	}

	saveFile(data: any, filename?: string) {
		saveAs(new Blob([data], { type: "application/pdf" }), filename);
	};

	acceptRegulation() {
		if (this.regulationOpened || this.acceptedRegulation) {
			this.apiService
				.acceptRegulationUsingPATCH(this.auction.idAuction)
				.subscribe((/* value */) => {
					this.acceptedRegulation = true;
				});
		} else {
			this.snackBarService.show({
				status: "info",
				titlePlaceholder: "auction.details.messageWarning",
			}, 1e4);
		}
	}

	saveAuctionMessage() {
		if (this.utils.isNullOrUndefined(this.auction.auctionMessage)) {
			this.auction.auctionMessage = "";
		}
		this.auctionService
			.editAuctionMessageUsingPATCH(
				this.auction.auctionMessage,
				this.auction.idAuction
			)
			.subscribe();
		if (!this.utils.isNullOrUndefined(this.uploadedFileId)) {
			this.auctionService
				.editFileMessageUsingPATCH(
					this.auction.idAuction,
					this.uploadedFileId
				)
				.subscribe(() => {
					this.uploadedFileId = null;
					this.apiService
						.getAuctionFullUsingGET(this.auction.idAuction)
						.subscribe(af => {
							this.mapAuction(af);
						});
				});
		}
		this.showAuctionMessage = true;
	}

	deleteAuctionFile() {
		this.auctionService
			.deleteFileMessageUsingDELETE(this.auction.idAuction)
			.subscribe(() => {
				this.auction.idFileMessage = null;
			});
	}

	/* 	search() {
			this.apiService
				.getAuctionFullSearchUsingGET(
					this.auction.idAuction,
					this.brand,
					this.licensePlate,
					this.model,
					this.countryArea,
					this.state,
					this.idVehicle,
					this.frame
				)
				.subscribe(value => {
					this.mapAuction(value);
				});
		}
	 */
	/* 	reset() {
			this.brand = this.model = this.licensePlate = this.countryArea = "";
			this.idVehicle = this.state = null;
			this.search();
		} */

	/** Returns all possible lot statuses.
	 * {@link started} is de facto redundant,
	 * since it is a global variable, directly retrievable (to avoid recalculating it everytime),
	 * but it's been added in order to remind it may need to be updated before calling this method
	 */
	private getExtendedLotStatus(started: boolean, lotDTO: { dateEnd: Date, active: boolean }) {
		return (
			started ?
				lotDTO.active && this.utils.isLotExtra(lotDTO, this.auction)
					? "EXTRA"
					: this.utils.getLotStatus(lotDTO)
				: this.auction.status);
	}

	private mapAuction(auc: AuctionFullDTO) {
		this.auctionFull = auc;
		this.auction = auc.auctionDTO;
		this.lots = auc.lotFullDTO;

		if (this.auction.flgMultiAuctioneer) {
			this.templateService
				.getLogoByProvenanceUsingGET(this.auction.provenances)
				.subscribe(value => {
					this.logoAuctioneer = value;
				});
		}

		this.sumMultiVehicles = this.sumSingleObserved = this.sumLotsObserved = 0;

		this.singleLot = [];
		this.multipleLot = [];
		this.started = this.isStarted();
		for (const lot of this.lots) {
			lot.lotDTO.actualPrice = lot.lotDTO.currentPrice || lot.lotDTO.startPrice;
			lot.lotDTO.calculatedStatus = this.getExtendedLotStatus(this.started, lot.lotDTO);

			if (lot.lotDTO.flgMultiItem) {
				lot.reservePrice = this.auction.reservePrice;
				this.multipleLot.push(lot);

				this.sumMultiVehicles += lot.vehiclesDTO.length;
				if (lot.observed) {
					this.sumLotsObserved++;
				}
			} else {
				if (lot.observed) {
					this.sumSingleObserved++;
				}
				// NOTE: copying lot data on vehicle
				lot.vehiclesDTO[0].idLot = lot.lotDTO.idLot;
				lot.reservePrice = this.auction.reservePrice;
				lot.vehiclesDTO[0].reservePrice = lot.reservePrice;
				lot.vehiclesDTO[0].basePrice = lot.lotDTO.basePrice;
				lot.vehiclesDTO[0].startPrice = lot.lotDTO.startPrice;
				lot.vehiclesDTO[0].currentPrice = lot.lotDTO.currentPrice;
				lot.vehiclesDTO[0].actualPrice = lot.lotDTO.actualPrice;
				lot.vehiclesDTO[0].calculatedStatus = lot.lotDTO.calculatedStatus;

				lot.vehiclesDTO[0].acceptedOffers = lot.lotDTO.acceptedOffers;
				lot.vehiclesDTO[0].idLot = lot.lotDTO.idLot;
				lot.vehiclesDTO[0].dateEnd = lot.lotDTO.dateEnd;
				lot.vehiclesDTO[0].active = lot.lotDTO.active;
				lot.vehiclesDTO[0].flgClickbuy = lot.lotDTO.flgClickbuy;
				lot.vehiclesDTO[0].clickbuyPrice = lot.lotDTO.clickbuyPrice;
				this.singleLot.push(lot.vehiclesDTO[0]);
			}
		}

		// this.calculateClose();

		this.mqttService.disconnect();
		this.mqttService.disconnected()
			.pipe(
				take(1),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				console.log("mqtt disconnected");
				this.showConnectionErrorModal();
			});
		this.mqttService.connect(true).subscribe(() => {
			this.subscribeToTopics();
		});
	}

	private showConnectionErrorModal = () => {
		if (!(this.destroyed || this.warningShown)) {
			this.warningShown = true;
			const dialogRef = this.dialog.open(this.connectError, { maxWidth: 400 })
			this.closeTemplateModal = () => {
				dialogRef.close();
				this.closeTemplateModal = () => { };
			};
		}
	}

	protected anyExtraLots = () => this.singleLot.some(l => l.calculatedStatus === "EXTRA") || this.multipleLot.some(l => l.lotDTO.calculatedStatus === "EXTRA");

	protected isDesktop = () => window.innerWidth > 991;
	protected getEndDate = () => new Date(this.auction.endDate);

	protected isLotClosed = (lot: LotDTO) => this.utils.getLotStatus(lot) === AuctionStatus.CLOSED;

	protected anyOpenLot = (lots: LotDTO[]) => lots.some(l => !this.isLotClosed(l));

	protected closeAuction() { this.auction.status = AuctionStatus.CLOSED; }

	// protected readonly getAuctionStatus = () => this.utils.getAuctionStatus(this.auction);

	anyBuyNow = () => this.lots.some(l => l.lotDTO.flgClickbuy && l.lotDTO.acceptedOffers === 0);

	uploadFile(files: FileList) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = "400px";
		if (files[0].name.endsWith(".pdf")) {
			this.fileService.uploadFileUsingPOST(files[0])
				.subscribe(value => {
					this.uploadedFile = files[0];
					this.uploadedFileId = value;

					this.snackBarService.show({
						titlePlaceholder: "dialog.success.message",
						// messagePlaceholder: "dialog.success.title"
					});
				});
		} else {
			this.snackBarService.show({
				status: "error",
				titlePlaceholder: "error.600002"/*,
				messagePlaceholder: "dialog.success.title" */
			});
		}
	}

	private subscribeToTopics() {
		this.started = this.isStarted();
		for (const lot of this.singleLot) {
			this.mqttService.subscribe(
				this.mqttService.topic(this.auction.idAuction, lot.idLot),
				msg => {
					const json = MqttResponse.from(JSON.parse(msg.payloadString));
					// console.log(json);
					lot.actualPrice = (lot.currentPrice = json.price) || lot.startPrice;
					lot.acceptedOffers = json.acceptedOffers;
					lot.dateEnd = new Date(json.endTimeForItem);
					lot.active = json.active;
					lot.calculatedStatus = this.getExtendedLotStatus(this.started, lot as { dateEnd: Date, active: boolean } /* NOTE: dateEnd just initialized */);
					if (json.overMinExpectedOffer) {
						lot.reservePrice = false;
						this.getLotBaseSingle(lot);
					}
					// this.calculateClose();
					// console.info("MQTT: refreshing filters ");
					this.singleLot = this.singleLot.slice(0); // NOTE: in order to trigger filters refresh
				}
			);
		}
		for (const lot of this.multipleLot) {
			this.mqttService.subscribe(
				this.mqttService.topic(
					this.auction.idAuction,
					lot.lotDTO.idLot
				),
				msg => {
					const json = MqttResponse.from(
						JSON.parse(msg.payloadString)
					);
					// console.log(json);
					lot.lotDTO.actualPrice = (lot.lotDTO.currentPrice = json.price) || lot.lotDTO.startPrice;
					lot.lotDTO.acceptedOffers = json.acceptedOffers;
					lot.lotDTO.dateLastOffer = json.managedTime;
					lot.lotDTO.dateEnd = new Date(json.endTimeForItem);
					lot.lotDTO.calculatedStatus = lot.lotDTO.calculatedStatus = this.getExtendedLotStatus(this.started, lot.lotDTO);
					if (json.overMinExpectedOffer) {
						lot.reservePrice = false;
						this.getLotBase(lot);
					}
					// this.calculateClose();
					// console.info("MQTT: refreshing filters ");
					this.multipleLot = this.multipleLot.slice(0); // NOTE: in order to trigger filters refresh
				}
			);
		}
	}

	isStarted(): boolean { return this.auction && new Date(this.auction.startDate).getTime() < Date.now(); }

	/*
		calculateClose(): any {
			let maxSingle = new Date(0),
				maxMulti = new Date(0);

			if (!this.utils.isEmpty(this.singleLot)) {
				maxSingle = new Date(
					this.singleLot
						.map(value => value.dateEnd)
						.reduce((a, b) => (a > b ? a : b))
				);
			}
			if (!this.utils.isEmpty(this.multipleLot)) {
				maxMulti = new Date(
					this.multipleLot
						.map(value => value.lotDTO.dateEnd)
						.reduce((a, b) => (a > b ? a : b))
				);
			}

			this.closed = (maxSingle > maxMulti ? maxSingle : maxMulti).getTime() < Date.now();
		} */

	getLotBaseSingle(lot: VehicleDTO) {
		if (this.utils.isNullOrUndefined(lot.basePrice)) {
			this.auctionService
				.getLotBasePriceUsingGET(lot.idLot)
				.subscribe(value => {
					lot.basePrice = value;
				});
		}
	}

	getLotBase(lot: LotVehicle) {
		if (this.utils.isNullOrUndefined(lot.lotDTO.basePrice)) {
			this.auctionService
				.getLotBasePriceUsingGET(lot.lotDTO.idLot)
				.subscribe(value => {
					lot.lotDTO.basePrice = value;
				});
		}
	}

	getFile() {
		const fileName = "auction_file.pdf";
		this.fileService
			.getPdfUsingGET(this.auction.idFileMessage, fileName)
			.subscribe((response: Blob) => {
				/* const link = document.createElement("a");
				link.href = URL.createObjectURL(response);
				link.download = fileName;
				link.click();
				setTimeout(() => URL.revokeObjectURL(link.href), 100); */
				this.saveFile(response, fileName);
			});
	}

	hasPermission(permission: string): boolean {
		if (this.user) {
			return this.user.permission == permission;
		}
		this.userService.getProfileOfLoggedUser().subscribe(value => {
			this.user = value;
			return this.user.permission == permission;
		});
	}

	selectLogo = element =>
		this.utils.isEmpty(element.appraisalCode) ?
			this.utils.isEmpty(element.idPrincipalPhoto)
				? null
				: this.utils.getImageUrl(element.idPrincipalPhoto)
			: element.imageUrl;

	licPlaOrTarget = (country: string) => ["IT", "ES", "FR", "NL"].includes(country);

	ngOnDestroy(): void {
		if (!this.destroyed) {
			this.destroyed = true;
			this.mqttService.disconnect();
		}
	}

	clickAndBuy(element: any) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = "auto";

		this.dialog.open(
			ConfirmDialogComponent,
			dialogConfig
		).afterClosed().subscribe(result => {
			// tslint:disable-next-line:triple-equals
			if (result === "ok") {
				this.apiService
					.makeOffer(
						this.auction.idAuction,
						element.idLot,
						null,
						true
					)
					.subscribe((/* value */) => { });
			}
		});
	}

	// readonly searchName = signal("");
	// readonly isValid = computed(() => this.searchName().trim().length);

	// closeTemplateModal = () => { };

	/* 	submit() {
			if (this.isValid()) {
				// TODO: richiesta al server
				this.snackBarService.show({
					titlePlaceholder: "auction.search.saveSearchConfirmTitle",
					messagePlaceholder: "auction.search.saveSearchConfirmText"
				});
				this.searchName.set("");
				this.closeTemplateModal();
			}
		} */

	/* openTemplateModal(template: TemplateRef<any>, closeCallback?: (value: any) => void) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.maxWidth = "480px";

		const dialogRef = this.dialog.open(
			template,
			dialogConfig
		);
		if (closeCallback) {
			dialogRef.afterClosed().subscribe(closeCallback);
		}
		this.closeTemplateModal = () => {
			dialogRef.close();
			this.closeTemplateModal = () => { };
		};
	} */
}